<template>
  <div class="messaging-analytics-page" data-testid="messaging-analytics-page">
    <div v-if="error" class="mb-3">
      <inline-message severity="error" :closable="false">{{ error }}</inline-message>
    </div>

    <div v-if="isLoading && !summaryData" class="text-center">
      <h3 style="margin: 100px">
        <spinner style="width: 1rem; height: 1rem" strokeWidth="4" /> {{ t('Carregando') }}
      </h3>
    </div>

    <div v-else-if="summaryData" :class="{ 'content-refreshing': isLoading }">
      <MessagingAnalyticsKpiCards
        :totais="summaryData.totais"
      />

      <div class="grid mt-3">
        <div class="col-12">
          <MessagingAnalyticsCharts
            :chartData="chartData"
            :isLoading="isLoadingChart"
          />
        </div>
      </div>

      <div class="grid mt-3">
        <div class="col-12">
          <MessagingAnalyticsUnansweredChart
            :data="unansweredByDayData"
            :isLoading="isLoadingUnanswered"
          />
        </div>
      </div>

      <div class="mt-3">
        <MessagingAnalyticsDeviceTable
          :devices="summaryData.por_dispositivo"
        />
      </div>
    </div>

    <div v-else class="flex flex-column align-items-center p-5">
      <i class="pi pi-chart-bar text-4xl text-color-secondary mb-3"></i>
      <p class="text-color-secondary">{{ t('Nenhum dado disponivel. Verifique se ha dispositivos de mensageria configurados.') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue';
import type { Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useMessagingAnalytics } from '../../composables/useMessagingAnalytics';
import { log, error as logError } from '../../utils/DebugLogger';
import MessagingAnalyticsKpiCards from './MessagingAnalyticsKpiCards.vue';
import MessagingAnalyticsCharts from './MessagingAnalyticsCharts.vue';
import MessagingAnalyticsUnansweredChart from './MessagingAnalyticsUnansweredChart.vue';
import MessagingAnalyticsDeviceTable from './MessagingAnalyticsDeviceTable.vue';

interface SummaryShape {
  totais: Record<string, unknown>;
  por_dispositivo?: Array<Record<string, unknown>>;
}

const P = 'MessagingAnalyticsPage';
const { t } = useI18n();

log(P, 'setup', 'Script setup executing...');

const {
  summaryData: summaryDataRaw,
  chartData,
  unansweredByDayData,
  isLoading,
  isLoadingChart,
  isLoadingUnanswered,
  error,
  fetchDashboardSummary,
  fetchChartData,
  fetchUnansweredByDay
} = useMessagingAnalytics();

const summaryData = summaryDataRaw as unknown as Ref<SummaryShape | null>;

log(P, 'setup', 'useMessagingAnalytics() initialized');
log(P, 'setup', 'Initial refs - summaryData:', summaryData.value, 'chartData:', chartData.value, 'isLoading:', isLoading.value, 'error:', error.value);

let refreshInterval: ReturnType<typeof setInterval> | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const loadDashboard = async (source = 'unknown'): Promise<void> => {
  log(P, 'loadDashboard', 'called from:', source);
  log(P, 'loadDashboard', 'Current state - isLoading:', isLoading.value, 'error:', error.value, 'summaryData:', summaryData.value ? 'has data' : 'null');
  try {
    await Promise.all([
      fetchDashboardSummary(),
      fetchChartData('volume'),
      fetchUnansweredByDay()
    ]);
    log(P, 'loadDashboard', 'DONE');
    log(P, 'loadDashboard', 'summaryData:', summaryData.value ? JSON.stringify(summaryData.value).substring(0, 500) : 'null');
    log(P, 'loadDashboard', 'chartData:', chartData.value ? JSON.stringify(chartData.value).substring(0, 500) : 'null');
    log(P, 'loadDashboard', 'error:', error.value);
  } catch (err) {
    const e = err as Error;
    logError(P, 'loadDashboard', 'ERROR:', e.message);
    logError(P, 'loadDashboard', 'ERROR stack:', e.stack);
    logError(P, 'loadDashboard', 'After error - summaryData:', summaryData.value, 'error:', error.value);
  }
};

const debouncedLoadDashboard = (): void => {
  log(P, 'debouncedLoadDashboard', 'triggered (filter event)');
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    loadDashboard('filter-event-debounced');
  }, 300);
};

onMounted(() => {
  log(P, 'onMounted', 'mounting component');
  loadDashboard('onMounted');

  // Reload when global filter changes (debounced to avoid duplicate calls)
  eventBus.on('filter.updated', debouncedLoadDashboard);
  eventBus.on('filter.applied', debouncedLoadDashboard);

  // Auto-refresh every 5 minutes
  refreshInterval = setInterval(loadDashboard, 5 * 60 * 1000);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', debouncedLoadDashboard);
  eventBus.off('filter.applied', debouncedLoadDashboard);

  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
});
</script>

<style scoped>
.messaging-analytics-page {
  padding: 1rem;
}

.content-refreshing {
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.3s ease;
}
</style>
